import { getAPI } from 'apis/backend/apiNode';
import { comptabiliserPaiementAPI, deleteAttachmentAPI, deletePythonAPI, getAllAttchmentsAPI, getAPIById, getSingleAttachementAPI, paiementAPI, searchPythonAPI, updatePaiementAPI, uploadAttachmentAPI } from 'apis/backend/apiPython';
import Input from 'components/bootstrap/forms/Input';
import showNotification from 'components/extras/showNotification';
import Icon from 'components/icon/Icon';
import ImageVisualisation from 'components/ImageVisualisation';
import AuthContext from 'contexts/authContext';
import { Protected } from 'layout/Protected';
import { result } from 'lodash';
import moment from 'moment';
import { sortes } from 'pages/comptabilite/Clients/SingleFacture/PaiementPopup';
import { updateIntervention } from 'pages/planning/hooks/planningService';
import React, { useState, useMemo, useRef, useCallback, useEffect, useContext } from 'react';
import { Card, Badge, Button, Row, Col, Modal, Form, Image, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

export const journals = ['espèces', 'carte_bancaire', 'chèque', 'virement', 'autre'];

const PaiementsTab = ({ intervention, setIntervention }) => {
    const fileInputRef = useRef(null);
    const [paiementPython, setPaiementPython] = useState([])
    const { userData, socket } = useContext(AuthContext)
    // --- ÉTATS ---
    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [editingPaiement, setEditingPaiement] = useState(null);

    // Stockage des fichiers par Payment ID
    const [attachmentsMap, setAttachmentsMap] = useState({});

    // États pour le Modal (New vs Existing)
    const [newAttachments, setNewAttachments] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    // Liens paiement -> devis / factures
    const [paymentLinks, setPaymentLinks] = useState({});

    const [paiementForm, setPaiementForm] = useState({
        montant: '',
        methode_paiement: 'espèces',
        sorte: sortes[0],
        date_paiement: new Date().toISOString().split('T')[0],
        notes: '',
        reference: ''
    });

    const paiements = useMemo(() => intervention?.rapport_intervention?.paiements || [], [intervention]);

    // --- 1. FONCTION DE RÉCUPÉRATION API ---
    const fetchAttachmentsForPayment = useCallback(async (paymentId) => {
        if (!paymentId) return [];
        const moduleName = "paiements";
        try {
            const result = await getAllAttchmentsAPI(moduleName, paymentId);
            if (result && Array.isArray(result?.files)) {
                return result.files.map((fileName) => ({
                    id: fileName,
                    url: getSingleAttachementAPI(moduleName, paymentId, fileName),
                    isNew: false,
                }));
            }
        } catch (err) {
            console.error(`Erreur fichiers pour ${paymentId}:`, err);
        }
        return [];
    }, []);

    const [employeesMap, setEmployeesMap] = useState({});

    // Charger les liens devis/facture pour une liste d'IDs de paiement
    const loadLinks = useCallback(async (paymentIds) => {
        if (!paymentIds.length) return;
        try {
            // On construit un objet temporaire contenant les résultats
            const linksResult = await loadDevisAndFactureByPayments({ rapport_intervention: { paiement_ids: paymentIds } });
            const newLinksMap = {};
            linksResult.forEach(item => {
                newLinksMap[item.paymentId] = {
                    devis: item.devis,
                    facture_moves: item.facture_moves
                };
            });
            setPaymentLinks(prev => ({ ...prev, ...newLinksMap }));
        } catch (err) {
            console.error("Error loading links:", err);
        }
    }, []);

    const loadData = async () => {
        const legacyPaiements = intervention?.rapport_intervention?.paiements || [];
        const newPaiementIds = intervention?.rapport_intervention?.paiement_ids || [];
        console.log("newPaiementIds", newPaiementIds)
        // Combine both: extract payment_id from objects OR use the string ID directly
        const allUniqueIds = [
            ...new Set([
                ...legacyPaiements.map(p => p.payment_id).filter(id => id),
                ...newPaiementIds
            ])
        ];

        if (allUniqueIds.length === 0) return;

        const newAttachmentsMap = {};
        const newPaiementsPython = [];
        const newEmployeesMap = {};

        const promises = allUniqueIds.map(async (paymentId) => {
            try {
                // 1. Fetch Payment from Python
                const res = await getAPIById("paiements", paymentId);
                if (res) {
                    // Check uniqueness to prevent duplicates in state
                    if (!newPaiementsPython.find(p => p.id === res.id)) {
                        newPaiementsPython.push(res);
                    }

                    // 2. Fetch Employee details for the creator
                    const creatorId = res.created_by;
                    if (creatorId && !employeesMap[creatorId] && !newEmployeesMap[creatorId]) {
                        try {
                            const emp = await getAPI("user", "/" + creatorId);
                            if (emp) newEmployeesMap[creatorId] = emp.name || emp.nom;
                        } catch (e) { console.error("Erreur employé:", e); }
                    }
                }
            } catch (err) { console.error(`Erreur paiement ${paymentId}:`, err); }

            // 3. Fetch Attachments
            if (!attachmentsMap[paymentId]) {
                try {
                    const files = await fetchAttachmentsForPayment(paymentId);
                    newAttachmentsMap[paymentId] = files;
                } catch (err) { console.error(`Erreur attachments ${paymentId}:`, err); }
            }
        });

        await Promise.all(promises);

        // Update States
        if (newPaiementsPython.length > 0) {
            setPaiementPython(prev => {
                const combined = [...prev, ...newPaiementsPython];
                return combined.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
            });
        }

        if (Object.keys(newAttachmentsMap).length > 0) {
            setAttachmentsMap(prev => ({ ...prev, ...newAttachmentsMap }));
        }

        if (Object.keys(newEmployeesMap).length > 0) {
            setEmployeesMap(prev => ({ ...prev, ...newEmployeesMap }));
        }

        // Charger les liens pour les IDs récupérés
        await loadLinks(allUniqueIds);
    };

    const loadDevisAndFactureByPayments = async (intervention) => {
        const legacyPaiements = intervention?.rapport_intervention?.paiements || [];
        const paiementIds = intervention?.rapport_intervention?.paiement_ids || [];

        const allPaymentIds = [
            ...new Set([
                ...legacyPaiements.map(p => p.payment_id).filter(Boolean),
                ...paiementIds
            ])
        ];

        if (!allPaymentIds.length) return [];

        const results = [];

        await Promise.all(
            allPaymentIds.map(async (paymentId) => {
                try {
                    // 1. Find devis containing this paymentId in historique
                    const devisRes = await getAPI("devis", "/recherche/paiement/" + paymentId);
                    const devisList = devisRes?.data || [];

                    // 2. Find facture/payment moves
                    const paiementMoveFilters = {
                        and_conditions: [
                            { field: "payment_id", operator: "=", value: paymentId }
                        ]
                    };

                    const paiementMoveResult = await searchPythonAPI(
                        "paiements_move",
                        paiementMoveFilters
                    );

                    const payedPayments = (paiementMoveResult?.data || []).filter(
                        (payment) => payment?.state === "reconciled"
                    );

                    const moveIds = payedPayments
                        .map(p => p?.move_id)
                        .filter(Boolean);

                    results.push({
                        paymentId,
                        devis: devisList,
                        facture_moves: moveIds
                    });
                } catch (err) {
                    console.error("Error processing paymentId:", paymentId, err);
                }
            })
        );

        return results;
    };

    useEffect(() => {
        loadData();
    }, [paiements, fetchAttachmentsForPayment, getAPIById]);

    const handleEdit = (p) => {
        setEditingPaiement(p);
        // Determine if payment is linked to any devis or facture
        const links = paymentLinks[p.id] || { devis: [], facture_moves: [] };
        const hasLinks = links.devis.length > 0 || links.facture_moves.length > 0;
        setPaiementForm({
            montant: p?.amount,
            methode_paiement: p?.payment_method,
            date_paiement: moment(p?.date_payment).format("YYYY/MM/DD"),
            sorte: p?.sorte,
            // Store a flag to disable amount editing
            _hasLinks: hasLinks
        });

        const files = attachmentsMap[p?.id] || [];
        setExistingAttachments(files);
        setNewAttachments([]);
        setShowPaiementModal(true);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const filesWithPreview = files.map(file => {
            file.preview = URL.createObjectURL(file);
            return file;
        });
        setNewAttachments(prev => [...prev, ...filesWithPreview]);
    };

    const handleSavePaiement = async () => {
        if (!paiementForm.montant || parseFloat(paiementForm.montant) <= 0) {
            showNotification('Erreur', 'Montant invalide', 'warning');
            return;
        }
        setSpinner(true);

        try {
            const montant = parseFloat(paiementForm.montant);
            const companyId = intervention?.companyId?._id ?? intervention?.cars?.[0]?.companyId;

            // Check if editing and if amount changed and payment has links
            const targetId = editingPaiement?.payment_id || editingPaiement?.id;
            if (targetId && paiementForm._hasLinks) {
                const originalAmount = editingPaiement?.amount;
                if (originalAmount !== montant) {
                    showNotification('Erreur', 'Impossible de modifier le montant car ce paiement est déjà rattaché à un devis ou une facture.', 'warning');
                    setSpinner(false);
                    return;
                }
            }

            // 1. Prepare Data for Python API
            const pythonData = {
                payment_method: paiementForm.methode_paiement,
                amount: montant,
                date_payment: paiementForm.date_paiement,
                sorte: paiementForm.sorte,
            };

            let pythonPaymentId;

            if (targetId) {
                // UPDATE: Only send the modified fields (amount might be unchanged)
                await updatePaiementAPI(targetId, pythonData);
                pythonPaymentId = targetId;
            } else {
                // CREATE: Merge base data with "Create-Only" fields
                const createData = {
                    ...pythonData,
                    currency: 'EUR',
                    payment_type: 'inbound',
                    partner_type: 'customer',
                    partner_id: intervention?.client?._id,
                    partner_name: intervention?.client?.nom,
                    company_id: companyId,
                    ref_intervention: intervention?.reference || '',
                    created_by: userData?.id
                };

                const res = await paiementAPI(createData);
                pythonPaymentId = res?.id;
            }

            if (!pythonPaymentId) throw new Error("Sync with Python failed");

            // 2. Handle Attachments
            if (newAttachments.length > 0) {
                setUploading(true);
                const formData = new FormData();
                newAttachments.forEach(file => formData.append('files', file));
                formData.append('module', 'paiements');
                formData.append('record_id', pythonPaymentId);
                await uploadAttachmentAPI(formData);

                const updatedFiles = await fetchAttachmentsForPayment(pythonPaymentId);
                setAttachmentsMap(prev => ({ ...prev, [pythonPaymentId]: updatedFiles }));
            }

            // 3. Update MongoDB using 'paiement_ids'
            const currentIds = intervention?.rapport_intervention?.paiement_ids || [];
            let updatedIdsList = [...currentIds];

            if (!targetId && !updatedIdsList.includes(pythonPaymentId)) {
                updatedIdsList = [pythonPaymentId, ...updatedIdsList];
            }

            const updatePayload = {
                rapport_intervention: {
                    ...intervention.rapport_intervention,
                    paiement_ids: updatedIdsList
                },
            };

            await updateIntervention(intervention?._id, updatePayload);

            // 4. Update Local State
            setIntervention(prev => ({
                ...prev,
                rapport_intervention: {
                    ...prev.rapport_intervention,
                    paiement_ids: updatedIdsList
                }
            }));

            // UPDATE THE PYTHON LIST STATE
            setPaiementPython(prev => {
                if (targetId) {
                    return prev.map(p => (p.id === targetId ? { ...p, ...pythonData, id: targetId } : p));
                } else {
                    const newPaymentRecord = { ...pythonData, id: pythonPaymentId };
                    return [newPaymentRecord, ...prev];
                }
            });

            // Reload links for the new/updated payment
            await loadLinks([pythonPaymentId]);

            const formatPaymentType = (sorte) => {
                if (sorte === "acompte") return "un acompte";
                if (sorte === "total") return "un paiement total";
                if (sorte === "reste") return "un paiement du reste";
                return "un paiement";
            };

            const message = `${userData?.name} a ajouté ${formatPaymentType(
                paiementForm.sorte,
            )} de ${montant}€ par ${paiementForm.methode_paiement} dans l'intervention #${intervention?.reference || ""
                }`;

            socket.emit("addPayment", {
                title: "Nouveau paiement ajouté",
                message,
            });

            handleCloseModal();
            showNotification('Succès', 'Paiement synchronisé avec Python', 'success');
        } catch (err) {
            console.error("Save Error:", err);
            showNotification('Erreur', 'La sauvegarde a échoué', 'danger');
        } finally {
            setSpinner(false);
            setUploading(false);
        }
    };

    const handleCloseModal = () => {
        newAttachments.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
        setShowPaiementModal(false);
        setEditingPaiement(null);
        setNewAttachments([]);
        setExistingAttachments([]);
        setPaiementForm({ montant: '', methode_paiement: 'espèces', date_paiement: new Date().toISOString().split('T')[0], notes: '', reference: '' });
    };

    const handleDeletePaiement = async (pythonPaymentId) => {
        if (!pythonPaymentId) return;
        // Check if payment is linked
        const links = paymentLinks[pythonPaymentId] || { devis: [], facture_moves: [] };
        if (links.devis.length > 0 || links.facture_moves.length > 0) {
            showNotification('Impossible de supprimer', 'Ce paiement est rattaché à un devis ou une facture.', 'warning');
            return;
        }

        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce paiement et ses justificatifs ?")) return;

        setSpinner(true);
        try {
            // 1. DELETE FROM PYTHON & CLEAN ATTACHMENTS
            try {
                const filesToDelete = attachmentsMap[pythonPaymentId] || [];
                if (filesToDelete.length > 0) {
                    await Promise.all(
                        filesToDelete.map(file =>
                            deleteAttachmentAPI("paiements", pythonPaymentId.toString(), file.id)
                        )
                    );
                }

                await deletePythonAPI('paiements', `/${pythonPaymentId}`);

                setAttachmentsMap(prev => {
                    const { [pythonPaymentId]: removed, ...rest } = prev;
                    return rest;
                });
            } catch (pyErr) {
                console.error("Non-critical Python cleanup error:", pyErr);
            }

            // 2. PREPARE UPDATED LISTS
            const currentIds = intervention?.rapport_intervention?.paiement_ids || [];
            const updatedIdsList = currentIds.filter(id => id !== pythonPaymentId);

            // 3. UPDATE MONGODB
            const updatePayload = {
                rapport_intervention: {
                    ...intervention.rapport_intervention,
                    paiement_ids: updatedIdsList
                },
            };
            await updateIntervention(intervention?._id, updatePayload);

            // 4. UPDATE UI STATES
            setIntervention(prev => ({
                ...prev,
                rapport_intervention: {
                    ...prev.rapport_intervention,
                    paiement_ids: updatedIdsList
                }
            }));

            setPaiementPython(prev => prev.filter(p => p.id !== pythonPaymentId));

            // Remove links from state
            setPaymentLinks(prev => {
                const { [pythonPaymentId]: _, ...rest } = prev;
                return rest;
            });

            showNotification('Succès', 'Paiement supprimé', 'info');
        } catch (err) {
            console.error("Global deletion error:", err);
            showNotification('Erreur', 'La suppression a échoué', 'danger');
        } finally {
            setSpinner(false);
        }
    };
    const navigate = useNavigate();
    const handleValidPaiement = async (pythonPaymentId) => {
        if (!pythonPaymentId) return;
        if (!window.confirm("Êtes-vous sûr de vouloir Valider ce paiement et ses justificatifs ?")) return;

        setSpinner(true);
        try {
            await comptabiliserPaiementAPI(pythonPaymentId);
            setPaiementPython(prev => {
                return prev.map(p => (p.id === pythonPaymentId ? { ...p, state: "posted" } : p));
            });
        } catch (pyErr) {
            console.error("Non-critical Python cleanup error:", pyErr);
            showNotification('Erreur', 'La validation a échoué', 'danger');
        } finally {
            setSpinner(false);
        }
    };

    const deleteExistingAttachment = async (fileName) => {
        const recordId = editingPaiement?.payment_id || editingPaiement?.id;
        if (window.confirm("Voulez-vous supprimer ce fichier définitivement du serveur ?")) {
            try {
                await deleteAttachmentAPI("paiements", recordId?.toString(), fileName);
                setExistingAttachments((prev) =>
                    prev.filter((att) => att.id !== fileName)
                );
                setAttachmentsMap(prev => ({
                    ...prev,
                    [recordId]: prev[recordId]?.filter(file => file.id !== fileName)
                }));
                showNotification('Succès', 'Fichier supprimé du serveur', 'info');
            } catch (err) {
                console.error("Échec de la suppression:", err);
                showNotification('Erreur', 'Erreur lors de la suppression', 'danger');
            }
        }
    };

    const removeNewAttachment = (index) => {
        setNewAttachments((prev) => {
            const fileToRemove = prev[index];
            if (fileToRemove && fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.preview);
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                <h5 className="mb-0 fw-bold text-dark">Paiements</h5>
                <Button variant="primary" size="sm" className="rounded-pill px-3" onClick={() => setShowPaiementModal(true)}>
                    <i className="fas fa-plus me-2"></i>Nouveau paiement
                </Button>
            </div>

            {/* LISTE DES CARDS */}
            <Row className="g-3">
                {paiementPython.map((p, idx) => {
                    const paymentFiles = attachmentsMap[p.id] || [];
                    const coverImage = paymentFiles.length > 0 ? paymentFiles[0].url : null;
                    const links = paymentLinks[p.id] || { devis: [], facture_moves: [] };
                    const hasLinks = links.devis.length > 0 || links.facture_moves.length > 0;

                    return (
                        <Col key={p._id || idx} xs={12} sm={6} md={4}>
                            <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100">
                                <div className="bg-light d-flex align-items-center justify-content-center border-bottom" style={{ height: '150px', backgroundColor: '#f8f9fa', position: 'relative' }}>
                                    {p.state === "posted" && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '-35px',
                                            backgroundColor: '#198754',
                                            color: 'white',
                                            padding: '5px 40px',
                                            transform: 'rotate(45deg)',
                                            zIndex: 10,
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            letterSpacing: '1px'
                                        }}>
                                            Validé
                                        </div>
                                    )}
                                    {coverImage ? (
                                        <Image
                                            src={coverImage}
                                            className="w-100 h-100 object-fit-cover cursor-pointer"
                                            onClick={() => setSelectedImage(paymentFiles[0])}
                                            alt="Justificatif de paiement"
                                        />
                                    ) : (
                                        <div className="text-center p-3">
                                            {p.payment_id && !attachmentsMap[p.id] ? (
                                                <div className="d-flex flex-column align-items-center">
                                                    <Spinner animation="border" size="sm" variant="primary" className="mb-2" />
                                                    <span className="small text-muted">Récupération du fichier...</span>
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column align-items-center opacity-50">
                                                    <i className="fas fa-image-slash fa-2x mb-2 text-muted"></i>
                                                    <span className="small fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                                                        Aucune image trouvée
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <Card.Body className="p-3">
                                    <h5 className="fw-bold text-primary mb-1">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(p.amount)}</h5>
                                    <div className="">{moment(p.date_payment).format("DD/MM/YYYY")} - {p?.sorte} - {p.payment_method}</div>
                                    {p.created_by && (
                                        <div className="mt-2 d-flex align-items-center text-muted small">
                                            <i className="fas fa-user-circle me-1 text-secondary"></i>
                                            <span>Par : <strong>{employeesMap[p.created_by] || "Chargement..."}</strong></span>
                                        </div>
                                    )}

                                    {/* Tableau des liens (devis / factures) */}
                                    {(links.devis.length > 0 || links.facture_moves.length > 0) && (
                                        <div className="mt-3 border-top pt-2 small">
                                            <div className="fw-bold text-secondary mb-1">Lié à :</div>

                                            {links.devis.length > 0 && (
                                                <div className="mb-2">
                                                    <Badge bg="info" className="me-1">Devis</Badge>

                                                    {links.devis.map(d => (
                                                        <span key={d._id} className="d-inline-flex align-items-center me-2">
                                                            <span className="me-1">{d.reference}</span>

                                                            <a
                                                                href={`/devis/${d._id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-primary py-0 px-1"
                                                            >
                                                                Ouvrir
                                                            </a>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {links.facture_moves.length > 0 && (
                                                <div>
                                                    <Badge bg="success" className="me-1">Factures</Badge>

                                                    {links.facture_moves.map(move => (
                                                        <span key={move.id} className="d-inline-flex align-items-center me-2">
                                                            <span className="me-1">{move.name || "Pro Forma"}</span>

                                                            <a
                                                                href={`/factures/${move.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-success py-0 px-1"
                                                            >
                                                                Ouvrir
                                                            </a>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="mt-3 d-flex gap-2">
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="flex-grow-1"
                                            onClick={() => handleEdit(p)}
                                        >
                                            Modifier
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="flex-grow-1"
                                            onClick={() => handleDeletePaiement(p.id)}
                                            disabled={hasLinks} // Désactiver si lié
                                            title={hasLinks ? "Impossible de supprimer un paiement lié à un devis/facture" : ""}
                                        >
                                            Supprimer
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* MODAL AJOUT/EDIT */}
            <Modal show={showPaiementModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fs-5 fw-bold">{editingPaiement ? 'Modifier le paiement' : 'Nouveau paiement'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-0">
                    <Form>
                        <Row>
                            <Col md={12} className="mb-3">
                                <div className="bg-primary-subtle p-3 rounded-4 text-center">
                                    <label className="small fw-bold text-primary text-uppercase">Montant (€)</label>
                                    <Form.Control
                                        placeholder='0.00'
                                        type="number"
                                        className="text-center fw-bold border-0 bg-transparent fs-2 shadow-none text-primary"
                                        value={paiementForm.montant}
                                        onChange={(e) => setPaiementForm({ ...paiementForm, montant: e.target.value })}
                                        disabled={editingPaiement && paiementForm._hasLinks}
                                    />
                                    {editingPaiement && paiementForm._hasLinks && (
                                        <div className="text-danger small mt-1">Le montant ne peut pas être modifié car ce paiement est déjà rattaché à un devis/facture.</div>
                                    )}
                                </div>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="small fw-bold">Méthode</Form.Label>
                                <Form.Select value={paiementForm.methode_paiement} onChange={(e) => setPaiementForm({ ...paiementForm, methode_paiement: e.target.value })}>
                                    {journals?.map(j => <option key={j} value={j}>{j}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="small fw-bold">Type</Form.Label>
                                <Form.Select value={paiementForm.sorte} onChange={(e) => setPaiementForm({ ...paiementForm, sorte: e.target.value })}>
                                    {sortes?.map(j => <option key={j} value={j}>{j}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="small fw-bold">Date</Form.Label>
                                <Input type="date" value={moment(paiementForm.date_paiement).format('YYYY-MM-DD')} onChange={(e) => setPaiementForm({ ...paiementForm, date_paiement: e.target.value })} />
                            </Col>

                            {/* SECTION PIÈCES JOINTES */}
                            <div className="mt-4">
                                <Form.Label className="fw-bold small">Justificatifs</Form.Label>
                                <div className="border border-dashed p-3 text-center rounded-4 mb-3 bg-light cursor-pointer" onClick={() => fileInputRef.current.click()}>
                                    <input type="file" ref={fileInputRef} className="d-none" multiple onChange={handleFileChange} accept="image/*" />
                                    <i className="fas fa-cloud-upload-alt text-info fa-lg"></i>
                                    <p className="small mb-0 ms-2 d-inline">Ajouter des photos</p>
                                </div>

                                <div className="d-flex flex-wrap gap-3">
                                    {existingAttachments.map((att) => (
                                        <div key={att.id} className="position-relative border rounded p-1 bg-light" style={{ width: '120px' }}>
                                            <img
                                                src={att.url}
                                                className="img-fluid rounded cursor-pointer"
                                                style={{ height: '100px', width: '100%', objectFit: 'cover' }}
                                                onClick={() => setSelectedImage(att)}
                                                alt="Justificatif sauvegardé"
                                            />
                                            <div
                                                className="position-absolute top-0 end-0 bg-danger text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                                style={{ width: '22px', height: '22px', cursor: 'pointer', marginTop: '-8px', marginRight: '-8px', zIndex: 2 }}
                                                onClick={() => deleteExistingAttachment(att.id)}
                                                title="Supprimer définitivement"
                                            >
                                                <Icon icon="Close" size="xs" />
                                            </div>
                                            <div className="mt-1 text-center">
                                                <span className="badge bg-success-subtle text-success border border-success-subtle small w-100 py-1">
                                                    <i className="fas fa-check-circle me-1"></i>Sauvegardé
                                                </span>
                                            </div>
                                        </div>
                                    ))}

                                    {newAttachments.map((file, i) => {
                                        const localUrl = URL.createObjectURL(file);
                                        return (
                                            <div key={`new-${i}`} className="position-relative border border-primary-subtle rounded p-1 bg-white" style={{ width: '120px' }}>
                                                <img
                                                    src={localUrl}
                                                    className="img-fluid rounded cursor-pointer"
                                                    style={{ height: '100px', width: '100%', objectFit: 'cover' }}
                                                    onClick={() => setSelectedImage(localUrl)}
                                                    alt="Nouveau justificatif"
                                                />
                                                <div
                                                    className="position-absolute top-0 end-0 bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                                    style={{ width: '22px', height: '22px', cursor: 'pointer', marginTop: '-8px', marginRight: '-8px', zIndex: 2 }}
                                                    onClick={() => removeNewAttachment(i)}
                                                    title="Retirer"
                                                >
                                                    <Icon icon="Close" size="xs" />
                                                </div>
                                                <div className="mt-1 text-center">
                                                    <span className="badge bg-warning-subtle text-warning border border-warning-subtle small w-100 py-1">
                                                        <i className="fas fa-clock me-1"></i> À sauvegarder
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-0 shadow-sm p-3">
                    <Button variant="primary" className="w-100 fw-bold py-2 rounded-3" onClick={handleSavePaiement} disabled={spinner || uploading}>
                        {spinner && <Spinner size="sm" className="me-2" />}
                        {editingPaiement ? 'Mettre à jour' : 'Enregistrer le paiement'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* LIGHTBOX ZOOM */}
            {!!selectedImage && (
                <ImageVisualisation selectedImage={selectedImage} setSelectedImage={setSelectedImage} />
            )}
        </div>
    );
};

export default PaiementsTab;